import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import {
  CheckCircleIcon,
  CreditCardIcon,
  BuildingOfficeIcon,
  TruckIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { configurationService } from '../../services/configuration.service';

// Validation schemas
const authNetSchema = z.object({
  apiLoginId: z.string().min(1, 'API Login ID is required'),
  clientKey: z.string().min(1, 'Client Key is required'),
  transactionKey: z.string().min(1, 'Transaction Key is required'),
  signatureKey: z.string().optional(),
});

const mdiSchema = z.object({
  clientId: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
});

const curexaSchema = z.object({
  clientKey: z.string().min(1, 'Client Key is required'),
  clientSecret: z.string().min(1, 'Client Secret is required'),
});

type AuthNetFormData = z.infer<typeof authNetSchema>;
type MdiFormData = z.infer<typeof mdiSchema>;
type CurexaFormData = z.infer<typeof curexaSchema>;

interface OnboardingStepperProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function OnboardingStepper({ isOpen, onClose }: OnboardingStepperProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>({});

  const steps = [
    {
      key: 'authNet' as const,
      title: 'Payment Processing',
      subtitle: 'Authorize.net',
      description: 'Connect your payment gateway to process patient transactions securely.',
      icon: CreditCardIcon,
      helpText: 'You can find these credentials in your Authorize.net merchant account.',
    },
    {
      key: 'mdi' as const,
      title: 'Telemedicine Provider',
      subtitle: 'MD Integrations',
      description: 'Connect to enable telemedicine consultations for your patients.',
      icon: BuildingOfficeIcon,
      helpText: 'Contact MD Integrations support if you need API credentials.',
    },
    {
      key: 'curexa' as const,
      title: 'Pharmacy Fulfillment',
      subtitle: 'Curexa',
      description: 'Connect your pharmacy fulfillment partner for prescription delivery.',
      icon: TruckIcon,
      helpText: 'Contact Curexa to obtain your API credentials.',
    },
  ];

  // Forms
  const authNetForm = useForm<AuthNetFormData>({
    resolver: zodResolver(authNetSchema),
    defaultValues: { apiLoginId: '', clientKey: '', transactionKey: '', signatureKey: '' },
  });

  const mdiForm = useForm<MdiFormData>({
    resolver: zodResolver(mdiSchema),
    defaultValues: { clientId: '', clientSecret: '' },
  });

  const curexaForm = useForm<CurexaFormData>({
    resolver: zodResolver(curexaSchema),
    defaultValues: { clientKey: '', clientSecret: '' },
  });

  useEffect(() => {
    if (isOpen) {
      loadStatus();
    }
  }, [isOpen]);

  const loadStatus = async () => {
    try {
      setIsLoading(true);
      const configStatus = await configurationService.getConfigurationStatus();

      // Set completed steps based on configuration status
      const completed: Record<number, boolean> = {};
      if (configStatus.steps.authNet.configured) completed[0] = true;
      if (configStatus.steps.mdi.configured) completed[1] = true;
      if (configStatus.steps.curexa.configured) completed[2] = true;
      setCompletedSteps(completed);

      // Find first unconfigured step
      if (!configStatus.steps.authNet.configured) {
        setCurrentStep(0);
      } else if (!configStatus.steps.mdi.configured) {
        setCurrentStep(1);
      } else if (!configStatus.steps.curexa.configured) {
        setCurrentStep(2);
      } else {
        // All configured, go to last step
        setCurrentStep(2);
      }
    } catch (error) {
      // If error loading, just start from beginning
      setCurrentStep(0);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDismiss = () => {
    // Remove the flag so stepper doesn't show again
    localStorage.removeItem('show_onboarding');
    onClose();
  };

  const handleSaveStep = async () => {
    const step = steps[currentStep];
    let data: Record<string, unknown>;
    let isValid = false;

    try {
      setIsSaving(true);

      if (step.key === 'authNet') {
        isValid = await authNetForm.trigger();
        if (!isValid) {
          setIsSaving(false);
          return;
        }
        data = authNetForm.getValues();
      } else if (step.key === 'mdi') {
        isValid = await mdiForm.trigger();
        if (!isValid) {
          setIsSaving(false);
          return;
        }
        data = mdiForm.getValues();
      } else {
        isValid = await curexaForm.trigger();
        if (!isValid) {
          setIsSaving(false);
          return;
        }
        data = curexaForm.getValues();
      }

      await configurationService.updateIntegrationCredentials(step.key, data as any);
      
      setCompletedSteps((prev) => ({ ...prev, [currentStep]: true }));
      toast.success(`${step.subtitle} configured successfully!`);

      // Move to next step or complete
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // All steps done, dismiss
        handleDismiss();
        toast.success('Setup complete! You can update these settings anytime in Configurations.');
      }
    } catch (error) {
      toast.error(`Failed to save ${step.subtitle} credentials`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkipStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step, dismiss
      handleDismiss();
    }
  };

  const handleComplete = () => {
    handleDismiss();
    toast.success('Setup complete! You can update these settings anytime in Configurations.');
  };

  const allStepsComplete = Object.values(completedSteps).filter(Boolean).length === steps.length;

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />
      
      {/* Modal */}
      <div className="relative w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-accent-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <SparklesIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">Welcome to Capture Health!</h1>
                    <p className="text-white/80 text-sm">Let's set up your integrations</p>
                  </div>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  title="Skip for now"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Progress Steps */}
              <div className="flex items-center justify-center mt-6">
                {steps.map((step, index) => (
                  <div key={step.key} className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors cursor-pointer ${
                        completedSteps[index]
                          ? 'bg-white border-white text-primary-500'
                          : index === currentStep
                          ? 'border-white bg-white/20 text-white'
                          : 'border-white/40 bg-transparent text-white/40'
                      }`}
                      onClick={() => setCurrentStep(index)}
                    >
                      {completedSteps[index] ? (
                        <CheckCircleSolidIcon className="w-5 h-5" />
                      ) : (
                        <span className="text-sm font-semibold">{index + 1}</span>
                      )}
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-12 md:w-20 h-0.5 mx-2 rounded ${
                          completedSteps[index] ? 'bg-white' : 'bg-white/30'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-primary-50 rounded-xl">
                  <StepIcon className="w-8 h-8 text-primary-500" />
                </div>
                <div>
                  <div className="text-sm font-medium text-primary-500">{currentStepData.subtitle}</div>
                  <h2 className="text-xl font-bold text-gray-900">{currentStepData.title}</h2>
                  <p className="text-gray-600 mt-1">{currentStepData.description}</p>
                </div>
              </div>

              {/* Form Content */}
              <div className="space-y-4">
                {currentStepData.key === 'authNet' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="API Login ID"
                        {...authNetForm.register('apiLoginId')}
                        error={authNetForm.formState.errors.apiLoginId?.message}
                        placeholder="Enter API Login ID"
                      />
                      <Input
                        label="Client Key"
                        type={showPasswords.clientKey ? 'text' : 'password'}
                        {...authNetForm.register('clientKey')}
                        error={authNetForm.formState.errors.clientKey?.message}
                        placeholder="Enter Client Key"
                        rightIcon={
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('clientKey')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords.clientKey ? (
                              <EyeSlashIcon className="w-4 h-4" />
                            ) : (
                              <EyeIcon className="w-4 h-4" />
                            )}
                          </button>
                        }
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Transaction Key"
                        type={showPasswords.transactionKey ? 'text' : 'password'}
                        {...authNetForm.register('transactionKey')}
                        error={authNetForm.formState.errors.transactionKey?.message}
                        placeholder="Enter Transaction Key"
                        rightIcon={
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('transactionKey')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords.transactionKey ? (
                              <EyeSlashIcon className="w-4 h-4" />
                            ) : (
                              <EyeIcon className="w-4 h-4" />
                            )}
                          </button>
                        }
                      />
                      <Input
                        label="Signature Key (Optional)"
                        type={showPasswords.signatureKey ? 'text' : 'password'}
                        {...authNetForm.register('signatureKey')}
                        placeholder="Enter Signature Key"
                        rightIcon={
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('signatureKey')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords.signatureKey ? (
                              <EyeSlashIcon className="w-4 h-4" />
                            ) : (
                              <EyeIcon className="w-4 h-4" />
                            )}
                          </button>
                        }
                      />
                    </div>
                  </>
                )}

                {currentStepData.key === 'mdi' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Client ID"
                      {...mdiForm.register('clientId')}
                      error={mdiForm.formState.errors.clientId?.message}
                      placeholder="Enter Client ID"
                    />
                    <Input
                      label="Client Secret"
                      type={showPasswords.mdiSecret ? 'text' : 'password'}
                      {...mdiForm.register('clientSecret')}
                      error={mdiForm.formState.errors.clientSecret?.message}
                      placeholder="Enter Client Secret"
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('mdiSecret')}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.mdiSecret ? (
                            <EyeSlashIcon className="w-4 h-4" />
                          ) : (
                            <EyeIcon className="w-4 h-4" />
                          )}
                        </button>
                      }
                    />
                  </div>
                )}

                {currentStepData.key === 'curexa' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Client Key"
                      {...curexaForm.register('clientKey')}
                      error={curexaForm.formState.errors.clientKey?.message}
                      placeholder="Enter Client Key"
                    />
                    <Input
                      label="Client Secret"
                      type={showPasswords.curexaSecret ? 'text' : 'password'}
                      {...curexaForm.register('clientSecret')}
                      error={curexaForm.formState.errors.clientSecret?.message}
                      placeholder="Enter Client Secret"
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility('curexaSecret')}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.curexaSecret ? (
                            <EyeSlashIcon className="w-4 h-4" />
                          ) : (
                            <EyeIcon className="w-4 h-4" />
                          )}
                        </button>
                      }
                    />
                  </div>
                )}

                <p className="text-sm text-gray-500 mt-2">{currentStepData.helpText}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    disabled={isSaving}
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={handleSkipStep} disabled={isSaving}>
                  Skip {currentStep < steps.length - 1 ? 'this step' : 'for now'}
                </Button>
                {currentStep < steps.length - 1 ? (
                  <Button onClick={handleSaveStep} isLoading={isSaving}>
                    {completedSteps[currentStep] ? 'Update & Continue' : 'Save & Continue'}
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={allStepsComplete ? handleComplete : handleSaveStep} isLoading={isSaving}>
                    {allStepsComplete ? (
                      <>
                        Complete Setup
                        <CheckCircleIcon className="w-4 h-4 ml-2" />
                      </>
                    ) : completedSteps[currentStep] ? (
                      <>
                        Finish Setup
                        <CheckCircleIcon className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Save & Finish
                        <CheckCircleIcon className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Progress Summary */}
            <div className="flex items-center justify-center gap-6 p-4 bg-gray-100 text-sm text-gray-600">
              {steps.map((step, index) => (
                <div key={step.key} className="flex items-center gap-2">
                  {completedSteps[index] ? (
                    <CheckCircleSolidIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                  )}
                  <span className={completedSteps[index] ? 'text-green-700' : ''}>{step.subtitle}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
